import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { FiEdit2, FiTrash2 } from "react-icons/fi";

import GenericHeader from "../../../Componentes/GenericHeader";
import SortableTable from "../../../Componentes/Utils/SortableTable";
import FilterContainer from "../../../Componentes/Utils/FilterContainer";
import Filters from "../../../Componentes/Utils/Filters";
import MultiselectCheck from "../../../Componentes/Utils/MultiselectCheck";
import { formatCPF } from "../../../Funcoes/FormatCPF";
import useApi from "../../../hooks/useApi";
import { useModal } from "../../../contexts/ModalContext";
import { AuthContext } from "../../../Auth/AuthProvider";
import { VerifyPermission } from "../../../Funcoes/VerifyPermission";

import ModalEditarTeste from "./ModalEditarTeste";
import ModalDeleteTeste from "./ModalDeleteTeste";
import ModalViewTeste from "./ModalViewTeste";
import {
    formatarDataHora,
    formatResultadoTeste,
    formatTipoTeste,
    RESULTADO_TESTE_OPTIONS,
    TIPO_TESTE_OPTIONS,
} from "./utils";

const PAGE_SIZE = 100;
const SCROLL_THRESHOLD = 40;
const PERMISSAO_BASE = { modulo: "ssma", submodulo: "atendimento-amb" };

const PageContent = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    box-sizing: border-box;
    overflow: hidden;
`;

const Statistics = styled.div`
    width: 220px;
    flex-shrink: 0;
    background-color: #f3f3f3;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 10px;
    box-sizing: border-box;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const ContainerStatistic = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    justify-content: center;
    min-height: 100%;
`;

const TituloStatistic = styled.h3`
    text-align: center;
    margin: 0;
    padding: 0;
    font-size: 13px;
    font-weight: bold;
    color: #000;
`;

const TextoStatistic = styled.p`
    margin: 0;
    padding: 0;
    font-size: 13px;
    color: #333;

    span {
        font-weight: 700;
    }

    span.positivo { color: #b91c1c; }
    span.negativo { color: #15803d; }
`;

const TableFlexFill = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
`;

const AcoesCelula = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
`;

const BotaoAcao = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    color: ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
    background-color: ${(p) =>
        p.$danger ? "rgba(185, 28, 28, 0.12)" : "rgba(29, 78, 216, 0.10)"};
    border: 1px solid transparent;

    &:hover {
        background-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.22)" : "rgba(29, 78, 216, 0.20)"};
        border-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.5)" : "rgba(29, 78, 216, 0.45)"};
    }
`;

const ResultadoBadge = styled.span`
    display: inline-block;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.03em;
    color: ${(p) => (p.$positivo ? "#b91c1c" : "#15803d")};
    background-color: ${(p) =>
        p.$positivo ? "rgba(185, 28, 28, 0.12)" : "rgba(21, 128, 61, 0.12)"};
    border: 1px solid
        ${(p) => (p.$positivo ? "rgba(185, 28, 28, 0.35)" : "rgba(21, 128, 61, 0.35)")};
`;

function startOfDayMs(dateStr) {
    if (!dateStr) return null;
    return new Date(`${dateStr}T00:00:00`).getTime();
}
function endOfDayMs(dateStr) {
    if (!dateStr) return null;
    return new Date(`${dateStr}T23:59:59.999`).getTime();
}

function dataHoraToDayMs(value) {
    if (!value) return null;
    const datePart = String(value).slice(0, 10);
    return new Date(`${datePart}T00:00:00`).getTime();
}

function toValueSet(selected) {
    if (!Array.isArray(selected) || selected.length === 0) return null;
    return new Set(selected.map((s) => (typeof s === "object" ? s?.value : s)));
}

function uniqueSortedStrings(values) {
    return [...new Set(values.map((v) => String(v ?? "").trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, "pt-BR", { sensitivity: "base" })
    );
}

function aplicarFiltros(lista, filtros) {
    const nome = filtros.nome.trim().toUpperCase();
    const cpf = filtros.cpf.replace(/\D/g, "");
    const tDe = startOfDayMs(filtros.dataDe);
    const tAte = endOfDayMs(filtros.dataAte);
    const funcoesSet = toValueSet(filtros.funcoes);
    const centrosSet = toValueSet(filtros.centrosCusto);
    const contratosSet = toValueSet(filtros.contratos);
    const lideresSet = toValueSet(filtros.lideres);
    const tiposSet = toValueSet(filtros.tipos);
    const resultadosSet = toValueSet(filtros.resultados);

    return lista.filter((row) => {
        const rNome = String(row.nome ?? "").toUpperCase();
        const rCpf = String(row.cpf ?? "");
        if (nome && !rNome.includes(nome)) return false;
        if (cpf && !rCpf.includes(cpf)) return false;
        if (funcoesSet && !funcoesSet.has(String(row.funcao ?? ""))) return false;
        if (centrosSet && !centrosSet.has(String(row.centro_custo ?? ""))) return false;
        if (contratosSet && !contratosSet.has(String(row.contrato ?? ""))) return false;
        if (lideresSet && !lideresSet.has(String(row.lider ?? ""))) return false;
        if (tiposSet && !tiposSet.has(String(row.tipo_teste ?? ""))) return false;
        if (resultadosSet && !resultadosSet.has(String(row.resultado ?? ""))) return false;
        const t = dataHoraToDayMs(row.data_hora);
        if (tDe != null && (t == null || t < tDe)) return false;
        if (tAte != null && (t == null || t > tAte)) return false;
        return true;
    });
}

function calcularEstatisticas(lista) {
    const total = lista.length;
    let positivos = 0;
    let negativos = 0;
    for (const row of lista) {
        const r = String(row?.resultado ?? "").toUpperCase();
        if (r === "POSITIVO") positivos += 1;
        else if (r === "NEGATIVO") negativos += 1;
    }
    return { total, positivos, negativos };
}

const TestesAlcoolDrogasContent = forwardRef(function TestesAlcoolDrogasContent({ refreshKey = 0 }, ref) {
    const api = useApi();
    const { user } = useContext(AuthContext);
    const { openModal, closeModal, isModalOpen } = useModal();
    const tableRef = useRef(null);

    const podeLancarEditar = useMemo(
        () => VerifyPermission(user, { ...PERMISSAO_BASE, funcao: "testes-alcool-drogas-lancar-editar" }),
        [user]
    );
    const podeApagar = useMemo(
        () => VerifyPermission(user, { ...PERMISSAO_BASE, funcao: "testes-alcool-drogas-apagar" }),
        [user]
    );

    const [loading, setLoading] = useState(true);
    const [testes, setTestes] = useState([]);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    const [nome, setNome] = useState("");
    const [cpf, setCpf] = useState("");
    const [dataDe, setDataDe] = useState("");
    const [dataAte, setDataAte] = useState("");
    const [selectedFuncoes, setSelectedFuncoes] = useState([]);
    const [selectedCentrosCusto, setSelectedCentrosCusto] = useState([]);
    const [selectedContratos, setSelectedContratos] = useState([]);
    const [selectedLideres, setSelectedLideres] = useState([]);
    const [selectedTipos, setSelectedTipos] = useState([]);
    const [selectedResultados, setSelectedResultados] = useState([]);

    const [testeParaVisualizar, setTesteParaVisualizar] = useState(null);
    const [testeParaEditar, setTesteParaEditar] = useState(null);
    const [testeParaDelete, setTesteParaDelete] = useState(null);

    const filtros = useMemo(
        () => ({
            nome,
            cpf,
            dataDe,
            dataAte,
            funcoes: selectedFuncoes,
            centrosCusto: selectedCentrosCusto,
            contratos: selectedContratos,
            lideres: selectedLideres,
            tipos: selectedTipos,
            resultados: selectedResultados,
        }),
        [
            nome,
            cpf,
            dataDe,
            dataAte,
            selectedFuncoes,
            selectedCentrosCusto,
            selectedContratos,
            selectedLideres,
            selectedTipos,
            selectedResultados,
        ]
    );

    const filtered = useMemo(
        () => aplicarFiltros(testes, filtros),
        [testes, filtros]
    );

    useImperativeHandle(ref, () => ({
        getFilteredTestes: () => filtered,
    }), [filtered]);

    const estatisticas = useMemo(() => calcularEstatisticas(filtered), [filtered]);

    const opcoesFuncaoMs = useMemo(
        () => uniqueSortedStrings(testes.map((r) => r.funcao)).map((v) => ({ value: v, label: v })),
        [testes]
    );
    const opcoesCentroCustoMs = useMemo(
        () => uniqueSortedStrings(testes.map((r) => r.centro_custo)).map((v) => ({ value: v, label: v })),
        [testes]
    );
    const opcoesContratoMs = useMemo(
        () => uniqueSortedStrings(testes.map((r) => r.contrato)).map((v) => ({ value: v, label: v })),
        [testes]
    );
    const opcoesLiderMs = useMemo(
        () => uniqueSortedStrings(testes.map((r) => r.lider)).map((v) => ({ value: v, label: v })),
        [testes]
    );

    const initialFetch = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/Painel/API/AtendimentoAmb/TestesAlcoolDrogas/List/index.php");
            setTestes(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Erro ao listar testes:", err);
            setTestes([]);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        initialFetch();
    }, [initialFetch, refreshKey]);

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [filtros]);

    const loadMoreRows = useCallback(() => {
        const total = filtered.length;
        setVisibleCount((prev) => {
            if (prev >= total) return prev;
            return Math.min(prev + PAGE_SIZE, total);
        });
    }, [filtered.length]);

    const abrirVisualizacao = (row) => {
        if (!row?.id) return;
        setTesteParaVisualizar(row.id);
        openModal("testeView");
    };

    const fecharVisualizacao = () => {
        setTesteParaVisualizar(null);
        closeModal();
    };

    const abrirEdicao = (row) => {
        if (!podeLancarEditar) return;
        closeModal();
        setTesteParaEditar(row);
        openModal("testeEdit");
    };
    const abrirDelete = (row) => {
        if (!podeApagar) return;
        setTesteParaDelete(row);
        openModal("testeDelete");
    };

    const fecharEdicao = () => { setTesteParaEditar(null); closeModal(); };
    const fecharDelete = () => { setTesteParaDelete(null); closeModal(); };

    const columns = useMemo(
        () => [
            { id: "id", header: "ID", accessor: "id", sortable: true, width: "70px", align: "center" },
            {
                id: "cpf",
                header: "CPF",
                accessor: "cpf",
                sortable: true,
                width: "140px",
                render: (v) => (v ? formatCPF(String(v)) : "-"),
            },
            { id: "nome", header: "NOME", accessor: "nome", sortable: true },
            { id: "funcao", header: "FUNÇÃO", accessor: "funcao", sortable: true },
            { id: "centro_custo", header: "CENTRO CUSTO", accessor: "centro_custo", sortable: true, width: "130px" },
            { id: "contrato", header: "CONTRATO", accessor: "contrato", sortable: true, width: "120px" },
            { id: "lider", header: "LÍDER", accessor: "lider", sortable: true, width: "120px" },
            {
                id: "tipo_teste",
                header: "TIPO",
                accessor: "tipo_teste",
                sortable: true,
                width: "120px",
                render: (v) => formatTipoTeste(v),
            },
            {
                id: "resultado",
                header: "RESULTADO",
                accessor: "resultado",
                sortable: true,
                width: "130px",
                align: "center",
                render: (v) => (
                    v
                        ? (
                            <ResultadoBadge $positivo={String(v).toUpperCase() === "POSITIVO"}>
                                {formatResultadoTeste(v)}
                            </ResultadoBadge>
                        )
                        : "-"
                ),
            },
            {
                id: "data_hora",
                header: "DATA/HORA",
                accessor: "data_hora",
                sortable: true,
                width: "150px",
                render: (v) => formatarDataHora(v),
                sortValue: (row) => row.data_hora ?? "",
            },
            ...(podeLancarEditar || podeApagar
                ? [{
                    id: "acoes",
                    header: "AÇÕES",
                    sortable: false,
                    width: "90px",
                    align: "center",
                    render: (_, row) => (
                        <AcoesCelula>
                            {podeLancarEditar && (
                                <BotaoAcao
                                    title="Editar"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        abrirEdicao(row);
                                    }}
                                >
                                    <FiEdit2 size={14} />
                                </BotaoAcao>
                            )}
                            {podeApagar && (
                                <BotaoAcao
                                    $danger
                                    title="Remover"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        abrirDelete(row);
                                    }}
                                >
                                    <FiTrash2 size={14} />
                                </BotaoAcao>
                            )}
                        </AcoesCelula>
                    ),
                }]
                : []),
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [podeLancarEditar, podeApagar]
    );

    return (
        <>
            <PageContent>
                <GenericHeader responsive>
                    <Filters columns={5}>
                        <FilterContainer
                            type="text"
                            placeholder="NOME COLABORADOR"
                            valor={nome}
                            changeFunc={setNome}
                        />
                        <FilterContainer
                            type="text"
                            placeholder="CPF"
                            valor={cpf}
                            changeFunc={setCpf}
                            maxLength={14}
                        />
                        <MultiselectCheck
                            options={opcoesFuncaoMs}
                            selectedOptions={selectedFuncoes}
                            setSelectedOptions={setSelectedFuncoes}
                            placeholder="FUNÇÃO..."
                        />
                        <MultiselectCheck
                            options={opcoesCentroCustoMs}
                            selectedOptions={selectedCentrosCusto}
                            setSelectedOptions={setSelectedCentrosCusto}
                            placeholder="CENTRO CUSTO..."
                        />
                        <MultiselectCheck
                            options={opcoesContratoMs}
                            selectedOptions={selectedContratos}
                            setSelectedOptions={setSelectedContratos}
                            placeholder="CONTRATO..."
                        />
                        <MultiselectCheck
                            options={opcoesLiderMs}
                            selectedOptions={selectedLideres}
                            setSelectedOptions={setSelectedLideres}
                            placeholder="LÍDER..."
                        />
                        <MultiselectCheck
                            options={TIPO_TESTE_OPTIONS}
                            selectedOptions={selectedTipos}
                            setSelectedOptions={setSelectedTipos}
                            placeholder="TIPO..."
                        />
                        <MultiselectCheck
                            options={RESULTADO_TESTE_OPTIONS}
                            selectedOptions={selectedResultados}
                            setSelectedOptions={setSelectedResultados}
                            placeholder="RESULTADO..."
                        />
                        <FilterContainer
                            type="date"
                            placeholder="DATA — A PARTIR DE"
                            valor={dataDe}
                            changeFunc={setDataDe}
                        />
                        <FilterContainer
                            type="date"
                            placeholder="DATA — ATÉ"
                            valor={dataAte}
                            changeFunc={setDataAte}
                        />
                    </Filters>

                    <Statistics>
                        <ContainerStatistic>
                            <TituloStatistic>ESTATÍSTICAS</TituloStatistic>
                            <TextoStatistic>
                                TOTAL: <span>{estatisticas.total}</span>
                            </TextoStatistic>
                            <TextoStatistic>
                                POSITIVO: <span className="positivo">{estatisticas.positivos}</span>
                            </TextoStatistic>
                            <TextoStatistic>
                                NEGATIVO: <span className="negativo">{estatisticas.negativos}</span>
                            </TextoStatistic>
                        </ContainerStatistic>
                    </Statistics>
                </GenericHeader>

                <TableFlexFill>
                    <SortableTable
                        ref={tableRef}
                        columns={columns}
                        data={filtered}
                        limit={visibleCount}
                        onScrollReachEnd={loadMoreRows}
                        scrollReachThreshold={SCROLL_THRESHOLD}
                        isLoading={loading}
                        loadingText="Carregando testes..."
                        emptyText="Nenhum teste encontrado."
                        initialSort={{ key: "data_hora", direction: "desc" }}
                        getRowId={(row) => row.id}
                        onRowClick={abrirVisualizacao}
                    />
                </TableFlexFill>
            </PageContent>

            {isModalOpen("testeView") && testeParaVisualizar != null && (
                <ModalViewTeste
                    testeId={testeParaVisualizar}
                    fechar={fecharVisualizacao}
                />
            )}

            {podeLancarEditar && isModalOpen("testeEdit") && testeParaEditar != null && (
                <ModalEditarTeste
                    teste={testeParaEditar}
                    fechar={fecharEdicao}
                    onSaved={() => { fecharEdicao(); initialFetch(); }}
                />
            )}

            {podeApagar && isModalOpen("testeDelete") && testeParaDelete != null && (
                <ModalDeleteTeste
                    teste={testeParaDelete}
                    fecharModal={fecharDelete}
                    onDeleted={initialFetch}
                />
            )}
        </>
    );
});

export default TestesAlcoolDrogasContent;
